import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

interface Suggestion {
  id: number | string;
  description: string;
  status: string;
  priority?: 'high' | 'medium' | 'low';
}

interface ModuleSuggestionsListProps {
  modules: Array<{ moduleName: string; suggestions: Suggestion[] }>;
  statusFilter: string;
  getStatusColor: (status: string) => string;
  onManage: (s: Suggestion) => void;
  onRequestVerification: (s: Suggestion) => void;
  isSaving?: boolean;
}

export function ModuleSuggestionsList({ modules, statusFilter, getStatusColor, onManage, onRequestVerification, isSaving }: ModuleSuggestionsListProps) {
  const moduleLabelMap: Record<string, string> = {
    "Public Transport": "Trasporto pubblico",
    "Parking": "Parcheggio",
    "Entrance": "Ingresso",
    "Interior Navigation": "Orientamento interno",
    "Elevators & Lifts": "Ascensori e montacarichi",
    "Visual Aids": "Ausili visivi",
    "Wayfinding & Signage": "Orientamento e segnaletica",
    "Digital Accessibility": "Accessibilità digitale",
    "Audio Systems": "Sistemi audio",
    "Communication Systems": "Sistemi di comunicazione",
    "Staff Training": "Formazione del personale",
    "Technology Access": "Accesso alla tecnologia",
    "Guest Services": "Servizi agli ospiti",
    "Food & Dining": "Ristorazione",
    "Recreation Areas": "Aree ricreative",
  };

  const getModuleLabel = (name: string) => moduleLabelMap[name] || name;

  if (!modules.length) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p className="text-s">Nessun modulo disponibile per questa categoria.</p>
      </div>
    );
  }

  const getStatusLabel = (status: string) => {
    if (status === 'in_progress') return 'in corso';
    if (status === 'verifica_in_corso') return 'verifica in corso';
    if (status === 'completed') return 'completato';
    if (!status) return 'non iniziato';
    return status;
  };

  const getPriorityLabel = (priority?: string) => {
    if (priority === 'high') return 'alta';
    if (priority === 'medium') return 'media';
    if (priority === 'low') return 'bassa';
    return priority || '';
  };

  return (
    <div className="space-y-4">
      {modules.map(({ moduleName, suggestions }) => {
        if (!Array.isArray(suggestions) || suggestions.length === 0) return null;
        const filtered = suggestions.filter((s) => statusFilter === 'all' || s.status === statusFilter);
        if (filtered.length === 0 && statusFilter !== 'all') return null;
        const displayModuleName = getModuleLabel(moduleName);

        return (
          <div key={moduleName} className="border rounded-lg">
            <div className="p-4 border-b">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-l font-semibold mb-1">{displayModuleName}</h3>
                  <p className="text-s text-muted-foreground">Modulo del Passaporto di Accessibilità con {suggestions.length} migliorament{suggestions.length !== 1 ? 'i' : 'o'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-ss">{suggestions.length} totali</Badge>
                  {filtered.length > 0 && <Badge variant="secondary" className="text-ss">{filtered.length} visibili</Badge>}
                </div>
              </div>
            </div>
            <div className="p-4">
              {filtered.length > 0 ? (
                <div className="space-y-3">
                  {filtered.map((suggestion) => (
                    <div key={suggestion.id} className="border rounded-lg p-4 bg-muted/20">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(suggestion.status)}>{getStatusLabel(suggestion.status)}</Badge>
                          {suggestion.priority && (
                            <Badge
                              variant="outline"
                              className={`text-ss ${suggestion.priority === 'high'
                                  ? 'border-red-200 bg-red-50 text-red-700'
                                  : suggestion.priority === 'medium'
                                    ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                                    : 'border-gray-200 bg-gray-50 text-gray-700'
                                }`}
                            >
                              Priorità {getPriorityLabel(suggestion.priority)}
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => onManage(suggestion)} disabled={isSaving}>Gestisci</Button>
                            {suggestion.status === 'verifica_in_corso' ? (
                              <Button variant="default" size="sm" className="bg-orange-100 text-orange-800 border border-orange-200" disabled>
                                ⏳ Verifica in corso
                              </Button>
                            ) : suggestion.status !== 'in_progress' ? (
                              <Button variant="default" size="sm" className="bg-gradient-travelin text-white" onClick={() => onRequestVerification(suggestion)} disabled={isSaving}>
                                Richiedi verifica
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <p className="text-s leading-relaxed">{suggestion.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <p className="text-s">{statusFilter === 'all' ? 'Nessun suggerimento disponibile per questo modulo.' : `Nessun suggerimento corrisponde al filtro stato "${statusFilter}" per questo modulo.`}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
